import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { FormSkeleton } from "..";
import { TableHeader } from "../datatable/TableHeader";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { Property } from "../../interfaces";
import { AppContext } from "../../context/index";
import { BoxFull, Button, Calendar } from "iuvity-ui-components";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Column } from 'primereact/column'
import { TenantCreatorSchema } from "../../validations/schemas";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from 'primereact/multiselect'
import { Dropdown } from 'primereact/dropdown'
import { useNavigate } from "react-router-dom";
import { tableBasicProps, columnBasicProps } from "../datatable/customTables/basicProps";

interface TenantsInfoProps {
  title: string;
  data: any[];
  actionFn: Function;
  reloadFn: Function;
};

const filterDefaultValues: {
  name: string;
  alias: string;
  primarycolor: string;
  secondarycolor: string;
  logoname: string;
  isotypelogoname: string;
  guid: string;
  tenantcode: string;
  parent: string;
} = {
  name: '',
  alias: '',
  guid: '',
  parent: '',
  tenantcode: '',
  primarycolor: '',
  secondarycolor: '',
  logoname: '',
  isotypelogoname: '',
};

export const RemovedTenants: React.FC<TenantsInfoProps> = ({
  title,
  data,
  actionFn,
  reloadFn,
}) => {
  const {
    t,
    appState: { language, selectedLabel, pageFilter,  selectedTenantId, tenantId },
    setAppState
  } = useContext(AppContext);
  const navigate = useNavigate();
  const tenantParentId = selectedTenantId || tenantId;

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          name: pageFilter?.name || '',
          alias: pageFilter?.alias || '',
          guid: pageFilter?.guid || '',
          parent: pageFilter?.parent || '',
          tenantcode: pageFilter?.tenantcode || '',
          primarycolor: pageFilter?.primarycolor || '',
          secondarycolor: pageFilter?.secondarycolor || '',
          logoname: pageFilter?.logoname || '',
          isotypelogoname: pageFilter?.isotypelogoname || '',
        }
      : filterDefaultValues,
  });

  const { control, setValue } = form;
  const nameValues = useWatch({
    control,
    name: "name",
  });

  const aliasValues = useWatch({
    control,
    name: "alias",
  });

  const guidValues = useWatch({
    control,
    name: "guid",
  });

  const parentValues = useWatch({
    control,
    name: "parent",
  });
  
  const tenantcodeValues = useWatch({
    control,
    name: "tenantcode",
  });

  const primarycolorValues = useWatch({
    control,
    name: "primarycolor",
  });

  const secondarycolorValues = useWatch({
    control,
    name: "secondarycolor",
  });

  const logonameValues = useWatch({
    control,
    name: "logoname",
  });

  const isotypelogonameValues = useWatch({
    control,
    name: "isotypelogoname",
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [filteredData, setFilteredData] = useState<any | null>(null);

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("Tenants.nameLabel"),
        },
        {
          field: "alias",
          header: t("Tenants.aliasLabel"),
        },
        {
          field: "guid",
          header: t("Tenants.guidLabel"),
        },
        {
          field: "parent",
          header: t("Tenants.parentLabel"),
        },
        {
          field: "tenantcode",
          header: t("Tenants.tenantcodeLabel"),
        },
        {
          field: "primarycolor",
          header: t("Tenants.primarycolorLabel"),
        },
        {
          field: "secondarycolor",
          header: t("Tenants.secondarycolorLabel"),
        },
        {
          field: "logoname",
          header: t("Tenants.logonameLabel"),
        },
        {
          field: "isotypelogoname",
          header: t("Tenants.isotypelogonameLabel"),
        },
        // {
        //   field: "status",
        //   header: t("Tenants.statusLabel"),
        // },
        {
          field: "updatedat",
          header: t("Tenants.updatedatLabel"),
        },
        {
          field: "actions",
          header: t("Tenants.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.IN },
    alias: { value: null, matchMode: FilterMatchMode.IN },
    guid: { value: null, matchMode: FilterMatchMode.IN },
    parent: { value: null, matchMode: FilterMatchMode.IN },
    tenantcode: { value: null, matchMode: FilterMatchMode.IN },
    primarycolor: { value: null, matchMode: FilterMatchMode.IN },
    secondarycolor: { value: null, matchMode: FilterMatchMode.IN },
    logoname: { value: null, matchMode: FilterMatchMode.IN },
    isotypelogoname: { value: null, matchMode: FilterMatchMode.IN },
  };
  const [filters, setFilters] = useState(defaultFilterValues);
  // const [updatedatValues, setUpdatedatValues] = useState();
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const onEditAction = async (prop: Property) => {
    await actionFn("REACTIVE", { ...prop, status: "DRAFT" });
    reloadFn();
  };

  const onAction = (type: "VIEW", tenant?: any) => {
    if (type === "VIEW" && tenant) {
      setAppState((prev) => ({
        ...prev,
        selectedTenant: tenant,
        tenants: data,
        edit: false
      }));
      navigate("/tenants/view");
    }
  };

  const onClearFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.IN },
      alias: { value: null, matchMode: FilterMatchMode.IN },
      guid: { value: null, matchMode: FilterMatchMode.IN },
      parent: { value: null, matchMode: FilterMatchMode.IN },
      tenantcode: { value: null, matchMode: FilterMatchMode.IN },
      primarycolor: { value: null, matchMode: FilterMatchMode.IN },
      secondarycolor: { value: null, matchMode: FilterMatchMode.IN },
      logoname: { value: null, matchMode: FilterMatchMode.IN },
      isotypelogoname: { value: null, matchMode: FilterMatchMode.IN },
    })
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  const onClearFiltersPerTable = ( options: any, filtersFromSession: any ) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      name: { value: options?.field === 'name' ? null : filtersFromSession?.name?.value, matchMode: FilterMatchMode.IN },
      alias: { value: options?.field === 'shortname' ? null : filtersFromSession?.shortname?.value, matchMode: FilterMatchMode.IN },
      primarycolor: { value: options?.field === 'primarycolor' ? null : filtersFromSession?.primarycolor?.value, matchMode: FilterMatchMode.IN },
      secondarycolor: { value: options?.field === 'secondarycolor' ? null : filtersFromSession?.secondarycolor?.value, matchMode: FilterMatchMode.IN },
      logoname: { value: options?.field === 'logoname' ? null : filtersFromSession?.logoname?.value, matchMode: FilterMatchMode.IN },
      isotypelogoname: { value: options?.field === 'isotypelogoname' ? null : filtersFromSession?.isotypelogoname?.value, matchMode: FilterMatchMode.IN },
      tenantcode: { value: options?.field === 'tenantcode' ? null : filtersFromSession?.tenantcode?.value, matchMode: FilterMatchMode.IN },
      guid: { value: options?.field === 'guid' ? null : filtersFromSession?.guid?.value, matchMode: FilterMatchMode.IN },
      parent: { value: options?.field === 'parent' ? null : filtersFromSession?.parent?.value, matchMode: FilterMatchMode.IN },
    });

    setValue(options?.field, '');
  };

  useEffect(() => {
    if (language) {
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(
            `Tenants.${col.field === "type" ? "prop" : ""}${col.field}Label`
          ),
        }))
      );
    }
  }, [t, language, setSelectedColumns]);

  useEffect(() => {
    if (data && !pageFilter) {
      setFilteredData(data);
    } else if (!data) {
      setFilteredData(null);
    }
  }, [data, setFilteredData, setAppState, selectedLabel, pageFilter]);

  useLayoutEffect(() => {
    if (data && pageFilter) {
      setFilteredData(
        data.filter(
          (item: any) => {
            return ((form.getValues("name")?.length > 0
              ? item.name.toLowerCase().includes(form.getValues("name").toLowerCase())
              : true) &&
              (form.getValues("alias")?.length > 0
                ? item.alias.toLowerCase().includes(form.getValues("alias").toLowerCase())
                : true) &&
              (form.getValues("primarycolor")?.length > 0
                ? item.tenantbranding.primarycolor.toLowerCase().includes(form.getValues("primarycolor").toLowerCase())
                : true) &&
              (form.getValues("secondarycolor")?.length > 0
                ? item.tenantbranding.secondarycolor.toLowerCase().includes(form.getValues("secondarycolor").toLowerCase())
                : true) &&
              (form.getValues("logoname")?.length > 0
                ? item.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name.toLowerCase().includes(form.getValues("logoname").toLowerCase())
                : true) &&
              (form.getValues("isotypelogoname")?.length > 0
                ? item.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name.toLowerCase().includes(form.getValues("isotypelogoname").toLowerCase())
                : true))
          }
        )
      );
    } else if (!data) {
      setFilteredData(data);
    }
  }, [data, setFilteredData, form, pageFilter]);

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  }
  const filterTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('filters-table') ?? '{}')?.filters;
    const arrayFiltersNotNull = !!filtersFromSession && Object.keys(filtersFromSession)?.filter((field: string) => !!filtersFromSession[field]?.value);
    let arrayFilterResult = data;
    if (!!arrayFiltersNotNull && arrayFiltersNotNull.length > 0) {
      for (let i=0; i < arrayFiltersNotNull.length; i++) {
        arrayFilterResult = arrayFilterResult?.filter((element: any) => filtersFromSession[arrayFiltersNotNull[i]]?.value?.includes((()=>{
          return (arrayFiltersNotNull[i] === 'logoname')
            ? element?.images.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name
            : (arrayFiltersNotNull[i] === 'isotypelogoname'
              ? element?.images.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name
              : ((arrayFiltersNotNull[i] === 'primarycolor' || arrayFiltersNotNull[i] === 'secondarycolor')
                ?  element?.tenantbranding[arrayFiltersNotNull[i]]
                : (arrayFiltersNotNull[i] === 'parent'
                    ? element?.parent?.name
                    : element[arrayFiltersNotNull[i]])));
        })()));
      };
    }

    const arrayOriginal = arrayFilterResult
      ?.filter((tenant: any) => tenant.status === 'DELETED')
      ?.map((tenant: any) =>
        ['primarycolor', 'secondarycolor'].includes(options.field)
        ? ({ label: tenant.tenantbranding ? tenant.tenantbranding[`${options.field}`] : '', value: tenant.tenantbranding ? tenant.tenantbranding[`${options.field}`] : '' })
        : ['logoname'].includes(options.field)
          ? ({ label: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name, value: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name })
          : ['isotypelogoname'].includes(options.field)
            ? ({ label: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name, value: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name })
            : ['parent'].includes(options.field)
              ? ({label: tenant?.parent?.name ? tenant?.parent?.name : false,
                value: tenant?.parent?.name ? tenant?.parent?.name : false})
              : ['guid'].includes(options.field)
                ? ({label: tenant?.guid ? tenant?.guid.substring(0,14) : false,
                  value: tenant?.guid ? tenant?.guid.substring(0,14) : false})
                : ({label: tenant[`${options.field}`], value: tenant[`${options.field}`]}))
      ?.filter((item: { label: string, value: string }) => item.label)
      ?.sort((a: any, b: any) => a.label?.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
        name={options.field}
        control={control}
        // rules={TenantCreatorSchema(t).name}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-1">
                {t(`Tenants.${options.field}Label`)}
              </label>
              <MultiSelect
                display="chip"
                options={arrayWithoutDuplicates}
                itemTemplate={ItemsTemplate}
                placeholder={t(`Tenants.${options.field}FilterPlaceholder`)}
                className="mb-2"
                emptyFilterMessage={t("Tenants.noAvailableMessage")}
                filter
                {...field}
              />
            </>
          );
        }}
      />
    )
  }

  const statuses = [
    `${t("Tenants.activeStatusLabel")}`,
    `${t("Tenants.inactiveStatusLabel")}`,
    `${t("Tenants.draftStatusLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  }
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Tenants.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Tenants.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  }

  const filterClearTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        disabled={filtersFromSession?.hasOwnProperty('global') ? !filtersFromSession[options?.field]?.value : true}
        onClick={() => onClearFiltersPerTable(options, filtersFromSession)}
      />
    )
  };
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Tenants.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            (options?.field === 'name' && nameValues.length > 0) ||
            (options?.field === 'alias' && aliasValues.length > 0) ||
            (options?.field === 'guid' && guidValues.length > 0) ||
            (options?.field === 'parent' && parentValues.length > 0) ||
            (options?.field === 'tenantcode' && tenantcodeValues.length > 0) ||
            (options?.field === 'primarycolor' && primarycolorValues.length > 0) ||
            (options?.field === 'secondarycolor' && secondarycolorValues.length > 0) ||
            (options?.field === 'logoname' && logonameValues.length > 0) ||
            (options?.field === 'isotypelogoname' && isotypelogonameValues.length > 0)
          )
        }
        onClick={options.filterApplyCallback}
      />
    )
  };

  const formatDate = (value: any) => {
    return new Date(value).toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };
  const dateBodyTemplate = (rowData: any) => {
    return formatDate(rowData?.updatedat);
  };
  // const dateFilterTemplate = (options: any) => {
  //   setUpdatedatValues(options.value);
  //   return (
  //     <Calendar
  //       label={t(`Tenants.${options.field}Label`)}
  //       value={options.value}
  //       onChange={(e) => options.filterCallback(e.value, options.index)}
  //       dateFormat="mm/dd/yy"
  //       placeholder="mm/dd/yyyy"
  //       mask="99/99/9999"
  //       className="mt-2 mb-2"
  //     />
  //   )
  // }

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      <div className="py-0">
        <BoxFull className="ml-0 mt-0 px-0" variant="tertiary">
          <div className="tablefilter-mc">

          </div>
        </BoxFull>
        <BoxFull className="dt-c mt-5 mb-4  mx-0">
          {data ? (
            <DataTable
              header={
                <TableHeader
                  title={t("Tenants.tableRemovedTenants")}
                  columns={columns}
                  selectedColumns={selectedColumns}
                  toggleColumns={setSelectedColumns}
                  filters={filters}
                  setFilters={setFilters}
                  reload={reloadFn}
                  onClearFiltersColumn={onClearFilters}
                  globalFilterValue={globalFilterValue}
                />
              }
              filters={filters}
              dataKey="id"
              value={filteredData?.filter((tenant: any) => tenant?.status === 'DELETED')
                .sort((tenantA: any, tenantB: any) => new Date(tenantB.updatedat).getTime() - new Date(tenantA.updatedat).getTime())
                .sort((tenantA: any, tenantB: any) => {
                  if (tenantA.id === tenantParentId) return -1;
                  return 0;
                })
                .map((tenant: any) => ({
                ...tenant,
                primarycolor: tenant?.tenantbranding?.primarycolor,
                secondarycolor: tenant?.tenantbranding?.secondarycolor,
                parent: tenant?.parent?.name || tenant.name,
                logoname: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name,
                isotypelogoname: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name,
                status: t(`Tenants.${tenant.status.toLowerCase()}StatusLabel`),
                updatedat: new Date(formatDate(tenant.updatedat)),
                actions: (
                  <div className="general-actions-datatable">
                    <Button
                      icon="iu"
                      className="p-button-rounded p-button-success p-button-outlined act-btn"
                      aria-label="Detail"
                      tooltip={t("TableActions.detailLabel")}
                      tooltipOptions={{ position: "bottom" }}
                      variant="p-button"
                      onClick={() => onAction('VIEW', tenant)}
                      style={{
                        color: "var(--successColor)",
                      }}
                    >
                      <i className="iu iu-icon-edit2 act-btn detail" />
                    </Button>
                  </div>
                ),
              }))}
              globalFilterFields={['name', 'alias', 'primarycolor', 'secondarycolor', 'logoname', 'isotypelogoname', 'parent', 'guid', 'tenantcode', 'updatedat']}
              filterDisplay="menu"
              { ...tableBasicProps(
                t,
                {
                  fieldBackgroundDynamic: '',
                  criteriaBackgroundDynamic: '',
                }
              )}
            >
              {selectedColumns.map((col) => {
                return (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable={!(col.field === "actions")}
                    align={col.field === "actions" ? "center" : "left"}
                    frozen={col.field === "actions" ? true : false}
                    alignFrozen={col.field === "actions" ? "right" : undefined}
                    filterField={col.field}
                    showFilterMatchModes={false}
                    // filterMenuStyle={{ width: '23rem'}}
                    filter={(col.field === "actions" || col.field === "updatedat") ? false : true}
                    filterElement={
                      (
                        col.field === "name" ||
                        col.field === "alias" ||
                        col.field === "primarycolor" ||
                        col.field === "secondarycolor" ||
                        col.field === "logoname" ||
                        col.field === "isotypelogoname" ||
                        col.field === "guid" ||
                        col.field === "parent" ||
                        col.field === "tenantcode"
                      ) && filterTemplate
                    }
                    filterApply={filterApplyTemplate}
                    filterClear={filterClearTemplate}
                    dataType={col.field === "updatedat" ? "date" : ""}
                    body={(rowData) => col.field === "updatedat"
                      ? dateBodyTemplate(rowData)
                      : <div>{rowData[col.field]}</div>}
                    { ...columnBasicProps({ col }) }
                  />
                );
              })}
            </DataTable>
          ) : (
            <FormSkeleton length={8} />
          )}
        </BoxFull>
      </div>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
